import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import HistoryService from "../../services/history";
import Loader from "../../assets/images/icon/LinkoSpinner.gif";

const TABS = ["text", "document", "audio", "chat"];

const TAB_LABELS = {
  text: "Text",
  document: "Document",
  audio: "Audio",
  chat: "AI Chat",
};

const History = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Read ?tab= from URL so the History button in Transcribe/Translate opens the right tab
  const urlTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(TABS.includes(urlTab) ? urlTab : "text");
  const [data, setData] = useState({ text: [], document: [], audio: [], chat: [] });
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [text, document, audio, chat] = await Promise.all([
          HistoryService.getTextTranslations().catch(() => ({ data: { data: [] } })),
          HistoryService.getDocumentTranslations().catch(() => ({ data: { data: [] } })),
          HistoryService.getAudioTranslations().catch(() => ({ data: { data: [] } })),
          HistoryService.getAIConversations().catch(() => ({ data: { data: [] } })),
        ]);
        setData({
          text: Array.isArray(text?.data?.data) ? text.data.data : [],
          document: Array.isArray(document?.data?.data) ? document.data.data : [],
          audio: Array.isArray(audio?.data?.data) ? audio.data.data : [],
          chat: Array.isArray(chat?.data?.data) ? chat.data.data : [],
        });
      } catch {
        // silently fail — empty lists shown
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const toggle = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const items = data[activeTab];

  const goToTab = () => {
    if (activeTab === "chat") navigate("/chat");
    else if (activeTab === "audio") navigate("/translate", { state: { tab: "transcribe" } });
    else navigate("/translate", { state: { tab: "translate" } });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full items-center overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 w-full max-w-[1176px] px-2 sm:px-4">

        {/* Tab bar */}
        <div className="flex items-center gap-2 mb-4 flex-shrink-0 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setExpandedIds(new Set()); }}
              className={`h-[33px] px-5 rounded-[67.52px] font-roboto font-bold text-[13px] uppercase leading-none transition-colors ${
                activeTab === tab
                  ? "bg-deep-purple text-white"
                  : "bg-white text-deep-purple border border-deep-purple/20"
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-[27px] p-4 flex flex-col flex-1 min-h-0 overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between w-full min-h-[47.6px] mb-3 flex-shrink-0">
            <div className="w-[80px]" />
            <span className="font-roboto font-black text-[14px] lg:text-[16px] text-[#42445A] uppercase leading-none tracking-normal">
              {TAB_LABELS[activeTab]} History
            </span>
            <button
              onClick={goToTab}
              className="h-[31px] px-4 rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none whitespace-nowrap"
            >
              Go to {TAB_LABELS[activeTab]}
            </button>
          </div>

          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <img src={Loader} alt="Loading..." className="h-16 w-16" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-[#3D2B90]/40 text-base font-medium">No {TAB_LABELS[activeTab]} history yet.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col gap-3 pr-1">
              {items.map((item) => {
                const id = item._id;
                const isOpen = expandedIds.has(id);

                if (activeTab === "text") return (
                  <HistoryCard key={id} isOpen={isOpen} onToggle={() => toggle(id)} date={formatDate(item.createdAt)} lang={item.targetLanguage}>
                    <Row label="Original" text={item.originalText} />
                    <Row label="Translation" text={item.translation} highlight />
                  </HistoryCard>
                );

                if (activeTab === "document") return (
                  <HistoryCard key={id} isOpen={isOpen} onToggle={() => toggle(id)} date={formatDate(item.createdAt)} lang={item.targetLanguage} fileUrl={item.fileUrl} fileType={item.fileType}>
                    <Row label="Extracted Text" text={item.extractedText} />
                    <Row label="Translation" text={item.translation} highlight />
                  </HistoryCard>
                );

                if (activeTab === "audio") return (
                  <HistoryCard key={id} isOpen={isOpen} onToggle={() => toggle(id)} date={formatDate(item.createdAt)} lang={item.targetLanguage} audioUrl={item.audioFileName || item.audioUrl || item.audioFile}>
                    <Row label="Transcription" text={item.transcription} />
                    <Row label="Translation" text={item.translation} highlight />
                  </HistoryCard>
                );

                if (activeTab === "chat") return (
                  <HistoryCard key={id} isOpen={isOpen} onToggle={() => toggle(id)} date={formatDate(item.createdAt)}>
                    {Array.isArray(item.messages) && item.messages.length > 0 ? (
                      item.messages.map((msg, idx) => (
                        <Row key={idx} label={msg.role === "user" ? "You" : "AI"} text={msg.content} highlight={msg.role !== "user"} />
                      ))
                    ) : (
                      <Row label="Conversation" text={JSON.stringify(item)} />
                    )}
                  </HistoryCard>
                );

                return null;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const resolveMediaUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("blob:") || trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  const cleanPath = trimmed.replace(/^\/+/, "");
  return base ? `${base}/${cleanPath}` : trimmed;
};

const HistoryCard = ({ isOpen, onToggle, date, lang, fileUrl, audioUrl, children }) => (
  <div className="border border-[#C7BFE4] rounded-[18px] overflow-hidden flex-shrink-0">
    {/* Card header — always visible */}
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-[#F3F1F9] hover:bg-[#EDE9F8] transition-colors text-left gap-3"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {lang && (
          <span className="flex-shrink-0 px-2.5 py-1 bg-[#3D2B90] text-white text-[10px] font-bold rounded-full uppercase">
            {lang}
          </span>
        )}
        {fileUrl && (
          <a
            href={resolveMediaUrl(fileUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 px-2.5 py-1 bg-[#EE7289] text-white text-[10px] font-bold rounded-full uppercase hover:opacity-80 transition-opacity"
          >
            View File
          </a>
        )}
        {audioUrl && (
          <a
            href={resolveMediaUrl(audioUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 px-2.5 py-1 bg-[#9B6DC5] text-white text-[10px] font-bold rounded-full uppercase hover:opacity-80 transition-opacity"
          >
            Audio
          </a>
        )}
        <span className="text-[#42445A] text-xs font-medium truncate">{date}</span>
      </div>
      <svg
        className={`flex-shrink-0 w-4 h-4 text-[#3D2B90] transition-transform ${isOpen ? "rotate-180" : ""}`}
        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    {/* Expandable body */}
    {isOpen && (
      <div className="px-4 py-4 flex flex-col gap-4 bg-white">
        {children}
      </div>
    )}
  </div>
);

const Row = ({ label, text, highlight }) => (
  <div className="flex flex-col gap-1">
    <span className={`text-[10px] font-bold uppercase tracking-wide ${highlight ? "text-[#3D2B90]" : "text-[#9CA3AF]"}`}>
      {label}
    </span>
    <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${highlight ? "text-[#1A0E3E]" : "text-[#42445A]"}`}>
      {text || "—"}
    </p>
  </div>
);

export default History;
